import { useEffect } from 'react';
import { useRef } from 'react';
import { useThree } from '@react-three/fiber';
import type { Graph3DBounds } from '../core/graph.types';
import type { Graph3DVector3 } from '../core/graph.types';
import type { PerspectiveCamera } from 'three';
import {
  resolveFitViewPlacement,
  type RuntimeOrbitControlsLike,
} from '../animation/viewportTween';

interface AutoFitCameraProps {
  bounds: Graph3DBounds;
  targetCenter: Graph3DVector3;
  enabled?: boolean;
}

export function AutoFitCamera({
  bounds,
  targetCenter,
  enabled = true,
}: AutoFitCameraProps) {
  const camera = useThree(state => state.camera) as PerspectiveCamera;
  const controls = useThree(state => state.controls) as unknown as
    | RuntimeOrbitControlsLike
    | undefined;
  const hasAppliedAutoFitRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      hasAppliedAutoFitRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (hasAppliedAutoFitRef.current) {
      return;
    }

    const { nextTarget, nextCameraPosition } = resolveFitViewPlacement({
      camera,
      bounds,
      targetCenter,
    });

    camera.position.set(
      nextCameraPosition.x,
      nextCameraPosition.y,
      nextCameraPosition.z
    );
    controls?.target.set(nextTarget.x, nextTarget.y, nextTarget.z);
    controls?.update();
    camera.lookAt(nextTarget.x, nextTarget.y, nextTarget.z);
    camera.updateProjectionMatrix();
    hasAppliedAutoFitRef.current = true;
  }, [
    bounds,
    camera,
    controls,
    enabled,
    targetCenter.x,
    targetCenter.y,
    targetCenter.z,
  ]);

  return null;
}
